// SPDX-License-Identifier: GPL-3.0-or-later
// The game flow of the page: start, pause, end, game over, and the frame loop.
//
// Flow: start screen -> start -> play -> game over -> play again. One engine plays one game. End
// game makes a new engine (fresh rules and a fresh seed), because the engine has no abort.
// Physics runs on the main thread: `engine.step(dt)` runs 1 ms steps inside (dt is capped at 100 ms).
// While a featured turn runs and the game is paused, the stream sends the same status every 5 s.
//
// Feedback: the flow tells the feedback layers (effects, sound, music, popups, touch) what happens,
// through the `FlowFeedback` port. The layers only read. They never change the game. The flow calls
// `feedback.frame` before the renderer draws a frame, and `feedback.tick` at every animation frame,
// also on the start screen and while paused, so the music and the popups move on in time.

import type { PinballRenderer } from "../src/render/index.ts";
import type {
  EngineEvent,
  GamePhase,
  GameStatus,
  InputAction,
  InputEvent,
  PinballEngine,
  RenderFrame,
} from "../src/shared/contracts.ts";
import { formatPoints } from "../src/table/callouts.ts";
import { STAGES } from "../src/table/run/stages.ts";
import {
  canPause,
  type DialogId,
  type OpenSource,
  openSet,
  padStartAction,
  pausesFirst,
} from "./dialog-policy.ts";
import { closeDialog, type Dialogs, modalOpen, openDialog } from "./dialogs.ts";
import { fillList, type ListSlot } from "./dom.ts";
import { routeEngineEvent } from "./events.ts";
import type { TurnSession } from "./featured-session.ts";
import type { BestResult } from "./high-score.ts";
import type { Hud, LiveFeed } from "./hud.ts";
import type { PowerGauge } from "./power.ts";
import { overStageLine } from "./run-text.ts";
import { isGameActive, isGameIdle } from "./screens.ts";
import { bestLine, featuredTurnCallout, featuredTurnLive, newBestLive } from "./text-cues.ts";

/** The longest frame time that the loop passes to the engine. The engine caps it again. */
export const MAX_FRAME_MS = 100;
/** How often a paused game offers the stream a heartbeat. The stream decides when to send. */
export const PAUSE_PUMP_MS = 1000;

/** What the feedback layers may read about the game. The flow changes the fields in place. */
export interface FlowSnapshot {
  phase: GamePhase;
  paused: boolean;
  ballsInPlay: number;
  /** True while a featured turn plays. */
  featured: boolean;
}

/** The port to the feedback layers. Every call is a notice: the layers cannot change the game. */
export interface FlowFeedback {
  /** Watch an engine and report its events to the feedback bus. The result stops the watch. */
  bridge(engine: PinballEngine): () => void;
  /** A physics frame is ready. Called before the renderer draws it. */
  frame(nowMs: number, dtMs: number, frame: RenderFrame): void;
  /** Called at every animation frame, also on the start screen and while paused. */
  tick(nowMs: number, game: Readonly<FlowSnapshot>): void;
  /** The game paused or resumed. */
  pause(paused: boolean): void;
  /**
   * The run holds play (true) or lets it go on (false). The flow sends a notice only at a change.
   * A missing method means the layers never hear of a hold.
   */
  hold?(held: boolean): void;
  /**
   * A game ended. `ended` is true when the player ended it (End game): the engine reports no
   * game over then, so the layers get one. The result tells if the score is a new best.
   */
  gameEnd(finalScore: number, ended: boolean): BestResult;
  /** A featured turn starts. The name is text only and it may be missing. */
  turnStart(name?: string): void;
}

/** The feedback port that does nothing. The flow uses it when the page gives none. */
export const NO_FEEDBACK: FlowFeedback = {
  bridge: () => () => {},
  frame: () => {},
  tick: () => {},
  pause: () => {},
  gameEnd: (finalScore) => ({ best: finalScore, isNew: false }),
  turnStart: () => {},
};

/** The port to the run (the HUD rows and the draft panel). The flow only gives notices. */
export interface FlowRunPort {
  /** A featured turn starts (true) or ends (false). */
  setFeatured(on: boolean): void;
  /** Called after each physics frame, after the HUD update. */
  frame(): void;
  /**
   * True while the run holds play (a draft shows). The flow sends no new press to the engine then.
   * A missing method means the run never holds play.
   */
  holds?(): boolean;
}

/** The part of the draft panel that the input route uses. */
export interface DraftTarget {
  readonly shown: boolean;
  action(action: InputAction): boolean;
}

/** The actions that the draft panel takes from the engine while it shows. */
const DRAFT_ACTIONS: ReadonlySet<InputAction> = new Set<InputAction>([
  "left_flipper",
  "right_flipper",
  "plunger",
]);

/**
 * The input route of the draft. While the panel shows, a flipper or plunger press goes to the
 * panel and never to the engine. The release of a press that the panel took goes nowhere too, so
 * a plunger that picked a card never fires the ball. A release of any other press always goes on
 * (the router returns false): the press started before the draft opened, so the engine and the
 * feedback layers hold that control. The release lifts it. The engine holds its ticks during the
 * draft, so the release moves nothing until the game resumes. It returns true when it took the
 * input. Other actions (start, nudge) are never taken. The press edge only: a press of an action
 * that the panel took already does nothing more.
 */
export function draftRouter(panel: DraftTarget): (ev: InputEvent) => boolean {
  const taken = new Set<InputAction>();
  return (ev) => {
    if (!DRAFT_ACTIONS.has(ev.action)) return false;
    if (!ev.pressed) return taken.delete(ev.action);
    if (taken.has(ev.action)) return true;
    if (!panel.shown) return false;
    taken.add(ev.action);
    panel.action(ev.action);
    return true;
  };
}

/** What the page gives the flow. */
export interface FlowDeps {
  readonly win: Window & typeof globalThis;
  readonly doc: Document;
  readonly stage: HTMLElement;
  readonly dialogs: Dialogs;
  readonly hud: Hud;
  readonly feed: LiveFeed;
  readonly view: PinballRenderer;
  readonly gauge: PowerGauge;
  readonly session: TurnSession;
  readonly makeEngine: () => PinballEngine;
  readonly openFeatured: () => void;
  /** Lift every held key, touch and pad button. */
  readonly releaseInputs: () => void;
  /** Read the pads. The loop calls it at each frame, also while the game is paused. */
  readonly pollPads: () => void;
  readonly overScore: HTMLElement;
  readonly overNote: HTMLElement;
  /** The line for the best score in the game over dialog. Missing: the page shows none. */
  readonly overBest?: HTMLElement;
  /** The list of the stages of the game in the game over dialog. Missing: the page shows none. */
  readonly overStages?: ListSlot;
  /** The run. Missing: no run rows, no draft. */
  readonly run?: FlowRunPort;
  readonly showError: (text: string) => void;
  /**
   * True while an alert holds the page (the graphics stopped, or the game stopped). No game starts
   * and no pause dialog closes then. Missing: the page never locks.
   */
  readonly locked?: () => boolean;
  /** The feedback layers. Missing: no effects, no sound. */
  readonly feedback?: FlowFeedback;
}

/** The game flow. */
export interface GameFlow {
  /** Send one input to the engine of now. */
  send(ev: InputEvent): void;
  /** True when no game runs (start screen or game over). A featured turn starts only then. */
  isIdle(): boolean;
  /** Pause the game when a dialog opens from the header menu during play. */
  pauseFor(dialog: DialogId, source: OpenSource): void;
  pause(): void;
  togglePause(): void;
  /** Start a free game. A featured socket that connects blocks it. */
  startFree(): void;
  /** The start button of a pad. */
  startFromPad(): void;
  endGame(): void;
  /** The Worker accepted the ticket of a featured turn. The name is text only and may be missing. */
  turnReady(resume: boolean, name?: string): void;
  /** The Worker ended the featured turn. */
  turnEnd(note: string): void;
  /** Draw the last frame again (after a resize). */
  redraw(): void;
  /** Show the first frame and start the loop. */
  begin(): void;
}

/** The state of the flow. */
interface State {
  engine: PinballEngine;
  unsubscribe: () => void;
  phase: GamePhase;
  paused: boolean;
  lastNow: number;
  lastFrame: RenderFrame | null;
  failed: boolean;
  /** The text of the game over dialog after a featured turn ended. */
  overNote: string;
  /** What the feedback layers read. It holds the same phase and pause flag as this state. */
  readonly snap: FlowSnapshot;
  /** The run holds play (a draft shows). The layers heard the last value. */
  held: boolean;
}

interface Ctx {
  readonly deps: FlowDeps;
  readonly s: State;
  readonly fb: FlowFeedback;
}

/** Set the phase in the state and in the snapshot of the feedback layers. */
function setPhase(s: State, phase: GamePhase): void {
  s.phase = phase;
  s.snap.phase = phase;
}

/** Read the run hold. The layers hear a change once, before the frame draws. */
function syncHold(ctx: Ctx): void {
  const held = ctx.deps.run?.holds?.() === true;
  if (held === ctx.s.held) return;
  ctx.s.held = held;
  ctx.fb.hold?.(held);
}

/** Set the pause flag in the state and in the snapshot, and tell the feedback layers. */
function setPaused(ctx: Ctx, paused: boolean): void {
  ctx.s.paused = paused;
  ctx.s.snap.paused = paused;
  ctx.fb.pause(paused);
}

function pauseGame(ctx: Ctx): void {
  if (!canPause(ctx.s.phase, ctx.s.paused)) return;
  setPaused(ctx, true);
  ctx.deps.releaseInputs();
  openDialog(ctx.deps.dialogs.pause);
}

function resumeGame(ctx: Ctx): void {
  if (!ctx.s.paused) return;
  setPaused(ctx, false);
  ctx.s.lastNow = ctx.deps.win.performance.now();
}

function isLocked(ctx: Ctx): boolean {
  return ctx.deps.locked?.() === true;
}

function togglePause(ctx: Ctx): void {
  if (!ctx.s.paused) pauseGame(ctx);
  else if (!isLocked(ctx)) closeDialog(ctx.deps.dialogs.pause);
}

function startGame(ctx: Ctx): void {
  const { s, deps } = ctx;
  // A locked page never runs a game: the canvas is dead or the game stopped.
  if (isLocked(ctx)) return;
  closeDialog(deps.dialogs.start);
  closeDialog(deps.dialogs.over);
  s.overNote = "";
  s.engine.start();
  const status = s.engine.status();
  setPhase(s, status.phase);
  s.snap.ballsInPlay = status.ballsInPlay;
  deps.hud.update(status);
  deps.stage.focus();
}

function startFree(ctx: Ctx): void {
  if (ctx.deps.session.blocksFreeStart()) {
    ctx.deps.feed.push("Your featured turn is connecting. Wait a moment.", "high");
    ctx.deps.openFeatured();
    return;
  }
  startGame(ctx);
}

/**
 * Send one input to the engine. The engine queues an input for its next physics tick. A draft
 * holds the ticks, so a press that comes in during the draft would act after the pick. The flow
 * drops such a press. A release always goes through, so a key that was down when the draft
 * opened still lifts in the engine.
 */
function sendInput(ctx: Ctx, ev: InputEvent): void {
  if (ev.pressed && ctx.deps.run?.holds?.() === true) return;
  ctx.s.engine.input(ev);
}

function startFromPad(ctx: Ctx): void {
  const action = padStartAction(openSet(ctx.deps.dialogs));
  if (action === "start_free_game") startFree(ctx);
  else if (action === "send_start") sendInput(ctx, { action: "start", pressed: true });
}

/**
 * Show the best score after a game: the line in the game over dialog, and, for a new best, one
 * sentence in the live region. A new best also has a popup, but a dialog covers the playfield.
 */
function showBest(ctx: Ctx, best: BestResult): void {
  const { overBest, feed } = ctx.deps;
  if (overBest !== undefined) {
    const line = bestLine(best);
    overBest.textContent = line;
    overBest.hidden = line === "";
  }
  const live = newBestLive(best);
  if (live !== null) feed.push(live, "high");
  showStages(ctx, best);
}

/** Fill the game over list with one line for each stage that ran. No stage: an empty list. */
function showStages(ctx: Ctx, best: BestResult): void {
  const { overStages } = ctx.deps;
  if (overStages === undefined) return;
  const lines: string[] = [];
  for (const stage of best.stages ?? []) {
    const def = STAGES[stage.index];
    if (def !== undefined) {
      lines.push(overStageLine(def, stage.points, stage.best, stage.isNew));
    }
  }
  fillList(overStages, lines);
}

function onEngineEvent(ctx: Ctx, ev: EngineEvent): void {
  const { s, deps } = ctx;
  routeEngineEvent(ev, {
    hud: deps.hud,
    feed: deps.feed,
    renderer: deps.view,
    now: () => deps.win.performance.now(),
  });
  deps.session.onEngineEvent(ev, s.engine.status().elapsedMs);
  if (ev.type !== "game_over") return;
  s.snap.featured = false;
  deps.run?.setFeatured(false);
  showBest(ctx, ctx.fb.gameEnd(ev.finalScore, false));
  deps.overScore.textContent = `FINAL SCORE ${formatPoints(ev.finalScore)}`;
  deps.overNote.textContent = s.overNote;
  deps.releaseInputs();
  openDialog(deps.dialogs.over);
}

/** Watch the engine. The bridge comes first, so the feedback bus gets `game_over` before the best score. */
function watchEngine(ctx: Ctx): void {
  const stopBridge = ctx.fb.bridge(ctx.s.engine);
  const stopFlow = ctx.s.engine.on((ev) => onEngineEvent(ctx, ev));
  ctx.s.unsubscribe = () => {
    stopFlow();
    stopBridge();
  };
}

/**
 * End the game now and go back to the start screen. In a featured turn the stream sends game_over
 * with the score so far. The engine has no abort, so a new engine takes the place of the old one.
 */
function endGame(ctx: Ctx): void {
  const { s, deps } = ctx;
  if (!isGameActive(s.phase)) return;
  const status = s.engine.status();
  deps.session.gameOver(status.score, status.elapsedMs);
  // The old engine reports no game over, so the feedback layers get it here (the lamps go back to
  // attract). It runs before `makeEngine`: the page then still holds the run of the ended game.
  const best = ctx.fb.gameEnd(status.score, true);
  s.unsubscribe();
  s.engine.dispose();
  s.engine = deps.makeEngine();
  watchEngine(ctx);
  deps.releaseInputs();
  setPaused(ctx, false);
  setPhase(s, "attract");
  s.snap.ballsInPlay = 0;
  s.snap.featured = false;
  deps.run?.setFeatured(false);
  showBest(ctx, best);
  s.overNote = "";
  s.lastFrame = null;
  deps.hud.clearTimer();
  deps.hud.setGoal("");
  deps.gauge.reset();
  deps.hud.update(s.engine.status());
  closeDialog(deps.dialogs.pause);
  openDialog(deps.dialogs.start);
}

/** One step of the game: physics, picture, HUD, and the stream. A failure stops the game. */
function stepGame(ctx: Ctx, dt: number, now: number): void {
  const { s, deps } = ctx;
  try {
    const f = s.engine.step(dt);
    s.lastFrame = f;
    // The effects move first, so the renderer draws them in this frame.
    ctx.fb.frame(now, dt, f);
    deps.view.render(f);
    deps.gauge.update(f);
    const status: GameStatus = s.engine.status();
    setPhase(s, status.phase);
    s.snap.ballsInPlay = status.ballsInPlay;
    deps.hud.update(status);
    deps.run?.frame();
    deps.session.pump(now, status);
  } catch (error) {
    s.failed = true;
    deps.releaseInputs();
    const name = error instanceof Error ? error.name : "error";
    deps.showError(`The game stopped (${name}). Reload the page to play again.`);
  }
}

function runFrame(ctx: Ctx, now: number): void {
  const { s, deps } = ctx;
  const dt = Math.min(MAX_FRAME_MS, Math.max(0, now - s.lastNow));
  s.lastNow = now;
  const live = !s.failed && !deps.doc.hidden;
  syncHold(ctx);
  // The pads are read in every frame, also while paused: the pause button resumes.
  if (live) deps.pollPads();
  if (live && !s.paused) stepGame(ctx, dt, now);
  deps.hud.tick(now);
  deps.feed.tick(now);
  // Every frame, also on the start screen and while paused: the music and the popups need the time.
  ctx.fb.tick(now, s.snap);
}

/** While the game is paused, the stream gets the same status as a heartbeat. */
function pausePump(ctx: Ctx): void {
  const { s, deps } = ctx;
  if (!s.paused || s.failed) return;
  deps.session.pump(deps.win.performance.now(), s.engine.status(), true);
}

function begin(ctx: Ctx): void {
  const { s, deps } = ctx;
  const loop = (now: number): void => {
    deps.win.requestAnimationFrame(loop);
    runFrame(ctx, now);
  };
  s.lastNow = deps.win.performance.now();
  deps.win.requestAnimationFrame(loop);
  // A timer covers a hidden tab: it has no animation frames, and the stream needs its heartbeat.
  deps.win.setInterval(() => pausePump(ctx), PAUSE_PUMP_MS);
  deps.hud.update(s.engine.status());
}

/** Auto pause: the window loses the focus or the tab hides. A dialog that is open keeps the game as it is. */
function bindAutoPause(ctx: Ctx): void {
  const { win, doc, dialogs } = ctx.deps;
  const autoPause = (): void => {
    if (!modalOpen(dialogs)) pauseGame(ctx);
  };
  dialogs.pause.addEventListener("close", () => resumeGame(ctx));
  win.addEventListener("blur", autoPause);
  doc.addEventListener("visibilitychange", () => {
    if (doc.hidden) autoPause();
  });
}

/** A featured turn starts: tell the layers, and write the text cue (the callout box and the live region). */
function announceTurn(ctx: Ctx, name: string | undefined): void {
  const { deps } = ctx;
  ctx.fb.turnStart(name);
  deps.hud.showCallout(featuredTurnCallout(name), deps.win.performance.now());
  deps.feed.push(featuredTurnLive(name), "high");
}

/** Make the flow. It watches the first engine and binds the auto pause. */
export function createGameFlow(deps: FlowDeps): GameFlow {
  const engine = deps.makeEngine();
  const ctx: Ctx = {
    deps,
    fb: deps.feedback ?? NO_FEEDBACK,
    s: {
      engine,
      unsubscribe: () => {},
      phase: "attract",
      paused: false,
      lastNow: 0,
      lastFrame: null,
      failed: false,
      overNote: "",
      snap: { phase: "attract", paused: false, ballsInPlay: 0, featured: false },
      held: false,
    },
  };
  watchEngine(ctx);
  bindAutoPause(ctx);
  return {
    send: (ev) => sendInput(ctx, ev),
    isIdle: () => isGameIdle(ctx.s.phase),
    pauseFor: (dialog, source) => {
      if (pausesFirst(dialog, source, ctx.s.phase, ctx.s.paused)) pauseGame(ctx);
    },
    pause: () => pauseGame(ctx),
    togglePause: () => togglePause(ctx),
    startFree: () => startFree(ctx),
    startFromPad: () => startFromPad(ctx),
    endGame: () => endGame(ctx),
    turnReady: (resume, name) => {
      ctx.s.snap.featured = true;
      deps.run?.setFeatured(true);
      if (!resume) {
        announceTurn(ctx, name);
        startGame(ctx);
      }
      closeDialog(deps.dialogs.featured);
    },
    turnEnd: (note) => {
      ctx.s.snap.featured = false;
      deps.run?.setFeatured(false);
      ctx.s.overNote = note;
      deps.overNote.textContent = note;
    },
    redraw: () => {
      if (ctx.s.lastFrame !== null) deps.view.render(ctx.s.lastFrame);
    },
    begin: () => begin(ctx),
  };
}
